'use client'

/**
 * FeatureFlagsContext — the ONE way a client component may read a server-side feature flag.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────────────────
 * The flags in `src/lib/feature-flags.ts` read `process.env.X` with no `NEXT_PUBLIC_` prefix. Next
 * does not inline those into the client bundle, so importing one into a CLIENT component gives:
 *     server render -> the real value from .env.local   (production has all four set to `true`)
 *     browser render -> undefined -> the `?? 'false'` default
 * The component then renders two different things and React throws a hydration error (#425 text
 * mismatch, plus #418/#423). That was live on the homepage until 2026-08-08, where HeroSection
 * rendered "5 free premium days (no card required)" on the server and "$24.99/month at our
 * early-access rate" in the browser.
 *
 * ⛔ THE OBVIOUS FIX IS WRONG. Renaming to `NEXT_PUBLIC_*` inlines the value at BUILD time, which
 * breaks the rollback feature-flags.ts documents in its own comments: "Go-live / instant rollback is
 * just an env change + restart (no code change, no redeploy)." A build-time constant needs a
 * redeploy. So the value is resolved on the SERVER per request (the root layout) and handed down.
 *
 * ── USAGE ────────────────────────────────────────────────────────────────────────────────────────
 *   const { freeTrialStart } = useFeatureFlags()      // in any client component
 * NEVER `import { FREE_TRIAL_START_ENABLED } from '@/lib/feature-flags'` inside a 'use client' file.
 * Server components should keep importing from lib/feature-flags directly; this is only the bridge.
 *
 * ⭐ DETECTOR for regressions of this whole class:
 *     grep -rln "from '@/lib/feature-flags'" src app components --include=*.tsx   # then check each for 'use client'
 *   then check each hit for 'use client'. Any client file reading a non-NEXT_PUBLIC_ env var at
 *   render time is this bug, and it is invisible until someone opens devtools.
 */

import React, { createContext, useContext, type ReactNode } from 'react'

export type FeatureFlags = {
  /** New accounts start with N free premium days (vs straight to paid copy). */
  freeTrialStart: boolean
  /** Subscription / daily-limit gating is active. */
  subscriptionGating: boolean
  /** Referral + affiliate program is live. */
  referral: boolean
  /** The member "invite friends, you both get premium days" loop is on (owner switch on /admin/integrations). */
  memberReferral: boolean
  /** Premium days EACH side gets when an invited friend verifies (MEMBER_REFERRAL_REWARD_DAYS). */
  memberReferralRewardDays: number
  /** Email verification is required / banner is shown. */
  emailVerification: boolean
  /**
   * 🚨 THE DONATION VALUES LIVE HERE FOR THE SAME REASON AS THE FLAGS, and they were the SECOND
   * outbreak of this exact bug (2026-08-10). `donationBonusActive()` / `donationMultiplierLabel()`
   * in lib/feature-flags read process.env.DONATION_MULTIPLIER and PREMIUM_GO_LIVE_DATE, neither
   * NEXT_PUBLIC_, so a client component that CALLS them during render gets the real value on the
   * server and a fallback in the browser. That is a hydration mismatch even though nothing here
   * looks like a "flag".
   * ⚠ A sweep of all 262 client components found FIVE files calling them in render, one of which
   * (components/AlertBanner.tsx) is mounted in the ROOT LAYOUT, so it broke hydration on EVERY page.
   * The first fix only covered HeroSection, which is why the errors never went away.
   */
  donationBonusActive: boolean
  donationMultiplierLabel: string
  /** Raw multiplier (2 pre-launch, 1 after). Same server-only origin as the two above. */
  donationMultiplier: number
  /**
   * Intro first-month price as a DISPLAY string (e.g. '9.99') when INTRO_FIRST_MONTH_ENABLED, else ''.
   * Empty = no intro line shown. Server-resolved for the same reason as the flags above (the env is not
   * NEXT_PUBLIC_ and must survive an env-only rollback). Register: pricing-lever-prescope-0905.
   */
  introFirstMonthPrice: string
  /** CHAT_DOCK_DEFAULT_CLOSED, server-resolved like the rest (src/lib/chatDockDefault.ts). */
  chatDockDefaultClosed: boolean
}

const FeatureFlagsContext = createContext<FeatureFlags | null>(null)

export function FeatureFlagsProvider({ flags, children }: { flags: FeatureFlags; children: ReactNode }) {
  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
}

/**
 * ⚠ THROWS when the provider is missing, deliberately, matching ExpContext in this codebase.
 * Returning silent defaults is how this bug behaved in the first place: the wrong branch rendered
 * and nobody noticed for weeks. A hard failure surfaces a missing provider on the first render
 * instead of shipping quietly-wrong pricing copy.
 */
export function useFeatureFlags(): FeatureFlags {
  const ctx = useContext(FeatureFlagsContext)
  if (!ctx) throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider (see app/layout.tsx)')
  return ctx
}
